import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DICTIONARIES, isValidLang, LANGS, SITE } from "@/i18n/translations";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LangContext = createContext(null);

// Deep-clone helper — cheap enough for our small dicts
function cloneDeep(v) {
  if (Array.isArray(v)) return v.map(cloneDeep);
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v)) o[k] = cloneDeep(v[k]);
    return o;
  }
  return v;
}

// Set a value at a dot-path (supports numeric segments for arrays)
function setByPath(root, path, value) {
  const parts = String(path).split(".");
  let cur = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    const nextKey = parts[i + 1];
    const nextIsIndex = /^\d+$/.test(nextKey);
    if (cur[k] === undefined || cur[k] === null) {
      cur[k] = nextIsIndex ? [] : {};
    }
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
}

// Build merged dictionaries from defaults + overrides array
function applyOverrides(defaults, entries) {
  const merged = {};
  for (const lang of Object.keys(defaults)) {
    merged[lang] = cloneDeep(defaults[lang]);
  }
  for (const e of entries || []) {
    if (!e || !merged[e.lang]) continue;
    try {
      setByPath(merged[e.lang], e.key, e.value);
    } catch (_) {
      /* bad path, skip */
    }
  }
  return merged;
}

export function LangProvider({ children }) {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const active = isValidLang(lang) ? lang : "en";

  const [overrides, setOverrides] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  // Fetch overrides once the guest is authenticated (endpoint is site-scoped)
  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get("/translations");
        if (!cancelled) setOverrides(data.entries || []);
      } catch (_e) {
        if (!cancelled) setOverrides([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, reloadTick]);

  const reloadTranslations = useCallback(() => setReloadTick((n) => n + 1), []);

  const mergedDicts = useMemo(() => applyOverrides(DICTIONARIES, overrides), [overrides]);
  const dict = mergedDicts[active];

  const t = useCallback(
    (path) => {
      const parts = String(path).split(".");
      let cur = dict;
      for (const p of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, p)) {
          cur = cur[p];
        } else {
          return path;
        }
      }
      return cur;
    },
    [dict]
  );

  const setLang = useCallback(
    (next) => {
      if (!isValidLang(next)) return;
      localStorage.setItem("em_lang", next);
      const rest = location.pathname.replace(/^\/[^/]+/, "") || "";
      navigate(`/${next}${rest}`);
    },
    [location.pathname, navigate]
  );

  const value = useMemo(
    () => ({ lang: active, t, setLang, langs: LANGS, dict, site: SITE, defaultDicts: DICTIONARIES, reloadTranslations }),
    [active, t, setLang, dict, reloadTranslations]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
